import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { ALLERGENS, ARTICLE_CATEGORIES, ARTICLE_UNITS } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, NumberField, SelectField, Field } from "@/components/common/Fields";
import { toast } from "sonner";

const EMPTY = { code: "", name: "", category: "Altro", unit: "kg", unit_cost: 0, allergens: {}, location_id: "", reorder_point: 0, min_stock: 0, max_stock: 0, active: true, notes: "" };

export function ArticleFormDialog({ open, onOpenChange, article, locations, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(article ? { ...EMPTY, ...article } : EMPTY); }, [open, article]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const cycle = (code) => setForm((f) => {
    const cur = f.allergens[code];
    const next = !cur ? "presente" : cur === "presente" ? "tracce" : undefined;
    const allergens = { ...f.allergens };
    if (next) allergens[code] = next; else delete allergens[code];
    return { ...f, allergens };
  });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, unit_cost: Number(form.unit_cost), reorder_point: Number(form.reorder_point), min_stock: Number(form.min_stock), max_stock: Number(form.max_stock), location_id: form.location_id || null };
      const r = article ? await api.put(`/articles/${article.id}`, payload) : await api.post("/articles", payload);
      if (r.data.revalidated_recipes?.length) toast.warning(`Allergeni cambiati: ${r.data.revalidated_recipes.length} ricette tornano Da validare (${r.data.revalidated_recipes.join(", ")})`, { duration: 8000 });
      else toast.success(article ? "Articolo aggiornato" : "Articolo creato");
      onOpenChange(false); onSaved(r.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="article-form-dialog">
        <DialogHeader><DialogTitle>{article ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle><DialogDescription>Se cambi gli allergeni, le ricette approvate che usano l'articolo tornano «Da validare».</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField label="Codice" value={form.code} onChange={set("code")} required testId="article-code-input" />
            <div className="sm:col-span-2"><TextField label="Nome" value={form.name} onChange={set("name")} required testId="article-name-input" /></div>
            <SelectField label="Categoria" value={form.category} onChange={set("category")} options={ARTICLE_CATEGORIES} testId="article-category-select" />
            <SelectField label="Unità" value={form.unit} onChange={set("unit")} options={ARTICLE_UNITS} testId="article-unit-select" />
            <NumberField label={`Costo unitario (€/${form.unit})`} value={form.unit_cost} onChange={set("unit_cost")} min={0} testId="article-cost-input" />
            <SelectField label="Ubicazione" value={form.location_id} onChange={set("location_id")} options={locations.map((l) => ({ value: l.id, label: l.name }))} testId="article-location-select" placeholder="Nessuna" />
            <NumberField label="Punto di riordino" value={form.reorder_point} onChange={set("reorder_point")} min={0} testId="article-reorder-input" />
            <NumberField label="Scorta minima" value={form.min_stock} onChange={set("min_stock")} min={0} testId="article-min-input" />
            <NumberField label="Scorta massima" value={form.max_stock} onChange={set("max_stock")} min={0} testId="article-max-input" />
          </div>
          <Field label="Allergeni (clic: assente → presente → tracce)">
            <div className="flex flex-wrap gap-1.5" data-testid="article-allergens">
              {ALLERGENS.map(([c, n]) => {
                const s = form.allergens[c];
                const cls = s === "presente" ? "badge-red" : s === "tracce" ? "badge-amber" : "border border-slate-200 bg-white text-slate-600";
                return <button type="button" key={c} onClick={() => cycle(c)} className={`badge ${cls} cursor-pointer select-none`} title={n} data-testid={`article-allergen-${c}`} aria-pressed={!!s}>{c}{s === "tracce" ? " · tracce" : s === "presente" ? " · presente" : ""}</button>;
              })}
            </div>
          </Field>
          <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button><Button type="submit" disabled={busy} data-testid="article-form-submit">{article ? "Salva" : "Crea articolo"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
